// components/ChatList.tsx
import type React from 'react';

import classes from '../Chat.module.scss';
import screen from '../ChatScreen.module.scss';
import type { ChatRole, ChatRoom } from '../types';
import { formatBulletinPrimaryLabel } from '../utils/formatBulletinLabel';
import { formatChatMessagePreview } from '../utils/formatChatMessagePreview';
import { formatShortName, formatDisplayName } from '../utils/formatUserName';
import { ChatListCard } from './ChatListCard';
import { useGlobalPresence } from '../store/GlobalPresenceContext';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

interface ChatListProps {
  chatRooms: ChatRoom[];
  allChatRooms?: ChatRoom[];
  /** Signed-in profile (only identity is needed here). */
  currentUser: { id: number } | null;
  currentUserId: number;
  isLoading: boolean;
  role: ChatRole;
  selectedChatId?: string;
  selectedBulletinId?: number | null;
  onChatSelect: (chatId: string, roomId: string, userId: number) => void;
  embedded?: boolean;
  showTitle?: boolean;
}

export const ChatList: React.FC<ChatListProps> = ({
  chatRooms,
  isLoading,
  role,
  selectedChatId,
  selectedBulletinId,
  onChatSelect,
  embedded = false,
  showTitle = true,
  currentUserId,
}) => {
  const { isUserOnline, isReady: presenceReady } = useGlobalPresence();
  const sidebarClass =
    role === 'tutor'
      ? classes['chat-list-sidebar--tutor']
      : classes['chat-list-sidebar--student'];

  const rootClass = embedded
    ? `${classes['chat-rooms-panel']} ${sidebarClass}`
    : `${classes['chat-list-sidebar']} ${sidebarClass}`;

  const titleNode = showTitle ? (
    embedded ? (
      <h2 className={`${classes['chat-rooms-title']} ${screen.listTitle}`}>Диалоги</h2>
    ) : (
      <h2 className={screen.listTitle}>Диалоги</h2>
    )
  ) : null;

  const needsBulletin = role === 'student' && embedded;

  if (isLoading) {
    return (
      <div className={rootClass}>
        {titleNode}
        <div className={classes['chat-list-loading']}>
          <BrandLoader center label='Загрузка...' />
        </div>
      </div>
    );
  }

  if (needsBulletin && selectedBulletinId == null) {
    return (
      <div className={rootClass}>
        {titleNode}
        <div className={classes['chat-list-empty']}>
          <p>Выберите заявку</p>
          <small>Чаты по выбранной заявке появятся здесь</small>
        </div>
      </div>
    );
  }

  if (chatRooms.length === 0) {
    return (
      <div className={rootClass}>
        {titleNode}
        <div className={classes['chat-list-empty']}>
          <p>У вас пока нет чатов</p>
          <small>Чаты открываются после отклика</small>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {titleNode}
      <div className={classes['chat-list-items']}>
        {chatRooms.map((room) => {
          const otherUser = room.other_user;
          const fullName = formatDisplayName(otherUser || {}, room.other_user_id);
          const bulletin = room.related_bulletins?.[0];
          const count = room.related_bulletins?.length ?? 0;
          const online = presenceReady
            ? isUserOnline(room.other_user_id)
            : Boolean(otherUser?.online);

          return (
            <ChatListCard
              key={`${room.id}-${room.room_id}`}
              name={formatShortName(otherUser || {}, room.other_user_id)}
              fullName={fullName}
              avatar={otherUser?.avatar}
              online={online}
              subject={bulletin ? formatBulletinPrimaryLabel(bulletin) : null}
              meta={
                count > 1
                  ? `${count} ${count < 5 ? 'заявки' : 'заявок'}`
                  : null
              }
              preview={
                room.moderation_blocked
                  ? 'Чат заблокирован'
                  : formatChatMessagePreview(
                      room.last_message?.text,
                      room.last_message?.sender_id,
                      currentUserId,
                    )
              }
              time={room.last_message?.timestamp}
              unread={room.unread_count}
              blocked={Boolean(room.moderation_blocked)}
              selected={selectedChatId === room.id}
              accent={role === 'tutor' ? 'tutor' : 'student'}
              onSelect={() => onChatSelect(room.id, room.room_id, room.other_user_id)}
            />
          );
        })}
      </div>
    </div>
  );
};
